/**
 * PATH: apps/ai/src/pages/SuggestionsPage.tsx
 * PURPOSE: Smart suggestions interface for quotes, categories, and pricing
 */

import { useState } from 'react';
import { Lightbulb, ThumbsUp, ThumbsDown, Copy, Check, Sparkles } from 'lucide-react';
import { cn } from '../lib/utils';

interface Suggestion {
  id: string;
  type: 'quote-item' | 'category' | 'pricing' | 'description';
  input: string;
  suggestions: Array<{
    text: string;
    confidence: number;
    source?: string;
  }>;
  selectedIndex?: number;
}

export default function SuggestionsPage() {
  const [input, setInput] = useState('');
  const [suggestionType, setSuggestionType] = useState<'quote-item' | 'category' | 'pricing' | 'description'>('quote-item');
  const [isLoading, setIsLoading] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([
    {
      id: '1',
      type: 'quote-item',
      input: 'Install new LED lighting in commercial office',
      suggestions: [
        { text: 'Supply and install LED panel lights 600x600mm - $185/unit', confidence: 0.94, source: 'Similar Job #4521' },
        { text: 'LED linear lighting strip installation - $120/m', confidence: 0.87, source: 'Price List 2024' },
        { text: 'Emergency exit light LED upgrade - $95/unit', confidence: 0.82, source: 'Similar Job #4498' },
      ],
    },
    {
      id: '2',
      type: 'category',
      input: 'Bathroom renovation waterproofing',
      suggestions: [
        { text: 'Waterproofing & Membrane', confidence: 0.96 },
        { text: 'Bathroom Renovation', confidence: 0.89 },
        { text: 'Tiling Preparation', confidence: 0.75 },
      ],
    },
  ]);

  const handleGenerate = () => {
    if (!input.trim()) return;
    
    setIsLoading(true);
    
    // Simulate AI generation
    setTimeout(() => {
      const newSuggestion: Suggestion = {
        id: Date.now().toString(),
        type: suggestionType,
        input: input,
        suggestions: generateMockSuggestions(input, suggestionType),
      };
      
      setSuggestions((prev) => [newSuggestion, ...prev]);
      setInput('');
      setIsLoading(false);
    }, 1500);
  };

  const generateMockSuggestions = (input: string, type: string) => {
    const baseSuggestions = {
      'quote-item': [
        { text: `Supply and install ${input} - standard grade`, confidence: 0.92, source: 'AI Generated' },
        { text: `${input} - premium specification`, confidence: 0.85, source: 'Similar Projects' },
        { text: `${input} with warranty package`, confidence: 0.78, source: 'Best Practice' },
      ],
      'category': [
        { text: 'General Construction', confidence: 0.88 },
        { text: 'Electrical Works', confidence: 0.82 },
        { text: 'Maintenance', confidence: 0.71 },
      ],
      'pricing': [
        { text: '$150-200/unit (based on similar jobs)', confidence: 0.91, source: 'Historical Data' },
        { text: '$180/unit (current market rate)', confidence: 0.87, source: 'Market Analysis' },
        { text: '$165/unit (competitive pricing)', confidence: 0.84, source: 'Competitor Analysis' },
      ],
      'description': [
        { text: `Complete ${input} including all labor and materials as per specifications.`, confidence: 0.93 },
        { text: `Professional ${input} service with quality assurance and cleanup.`, confidence: 0.86 },
        { text: `${input} - turnkey solution with project management included.`, confidence: 0.79 },
      ],
    };
    
    return baseSuggestions[type as keyof typeof baseSuggestions] || baseSuggestions['quote-item'];
  };

  const handleCopy = (id: string, text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const getTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      'quote-item': 'Quote Item',
      'category': 'Category',
      'pricing': 'Pricing',
      'description': 'Description',
    };
    return labels[type] || type;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Smart Suggestions</h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">
          AI-powered suggestions for quotes, categories, and pricing based on your data
        </p>
      </div>

      {/* Input Section */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-100 dark:border-gray-700">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-gradient-to-br from-orange-500 to-amber-500 rounded-lg text-white">
            <Sparkles className="w-5 h-5" />
          </div>
          <h2 className="font-semibold text-gray-900 dark:text-white">Generate Suggestions</h2>
        </div>

        {/* Type Selection */}
        <div className="flex flex-wrap gap-2 mb-4">
          {(['quote-item', 'category', 'pricing', 'description'] as const).map((type) => (
            <button
              key={type}
              onClick={() => setSuggestionType(type)}
              className={cn(
                "px-4 py-2 rounded-lg text-sm font-medium transition-colors",
                suggestionType === type
                  ? "bg-orange-500 text-white"
                  : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
              )}
            >
              {getTypeLabel(type)}
            </button>
          ))}
        </div>

        {/* Input Field */}
        <div className="flex gap-3">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={`Enter ${getTypeLabel(suggestionType).toLowerCase()} to get suggestions...`}
            className="flex-1 px-4 py-3 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500"
            onKeyDown={(e) => e.key === 'Enter' && handleGenerate()}
          />
          <button
            onClick={handleGenerate}
            disabled={!input.trim() || isLoading}
            className="px-6 py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-xl font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity flex items-center gap-2"
          >
            {isLoading ? (
              <>
                <Sparkles className="w-5 h-5 animate-pulse" />
                Generating...
              </>
            ) : (
              <>
                <Lightbulb className="w-5 h-5" />
                Generate
              </>
            )}
          </button>
        </div>
      </div>

      {/* Suggestions List */}
      <div className="space-y-4">
        {suggestions.map((suggestion) => (
          <div
            key={suggestion.id}
            className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden"
          >
            {/* Header */}
            <div className="px-6 py-4 bg-gray-50 dark:bg-gray-900 border-b border-gray-100 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-xs font-medium text-orange-600 bg-orange-100 dark:bg-orange-900/30 px-2 py-1 rounded">
                    {getTypeLabel(suggestion.type)}
                  </span>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                    Input: <span className="text-gray-900 dark:text-white">{suggestion.input}</span>
                  </p>
                </div>
              </div>
            </div>

            {/* Suggestions */}
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {suggestion.suggestions.map((item, index) => (
                <div
                  key={index}
                  className={cn(
                    "p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors",
                    suggestion.selectedIndex === index && "bg-green-50 dark:bg-green-900/20"
                  )}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <p className="text-gray-900 dark:text-white">{item.text}</p>
                      <div className="flex items-center gap-3 mt-2">
                        <span className="text-xs text-gray-500">
                          {(item.confidence * 100).toFixed(0)}% confidence
                        </span>
                        {item.source && (
                          <span className="text-xs text-orange-600">
                            Source: {item.source}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleCopy(suggestion.id + index, item.text)}
                        className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                        title="Copy"
                      >
                        {copiedId === suggestion.id + index ? (
                          <Check className="w-4 h-4 text-green-500" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </button>
                      <button
                        className="p-2 text-gray-400 hover:text-green-500"
                        title="Accept"
                      >
                        <ThumbsUp className="w-4 h-4" />
                      </button>
                      <button
                        className="p-2 text-gray-400 hover:text-red-500"
                        title="Reject"
                      >
                        <ThumbsDown className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

